<template>
  <Teleport to="body" :disabled="!teleportToBody">
    <Transition name="fade" appear>
      <div
        v-if="modelValue"
        class="loading-overlay"
        :class="{
          'full-screen': fullScreen,
          'admin-layout': adminLayout,
          teleported: teleportToBody,
        }"
        :style="adminLayoutStyles"
        role="status"
        :aria-label="ariaLabel"
      >
        <div class="loading-content">
          <div
            class="loading-spinner"
            :style="{ '--spinner-color': spinnerColor }"
          ></div>
          <div v-if="message" class="loading-text">{{ message }}</div>
          <div v-if="subtitle" class="loading-subtitle">{{ subtitle }}</div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, defineProps } from "vue";

const props = defineProps({
  /**
   * Controls the visibility of the loading overlay
   */
  modelValue: {
    type: Boolean,
    default: false,
  },
  /**
   * Main loading message to display
   */
  message: {
    type: String,
    default: "Loading...",
  },
  /**
   * Optional subtitle text
   */
  subtitle: {
    type: String,
    default: "",
  },
  /**
   * Color of the loading spinner
   */
  spinnerColor: {
    type: String,
    default: "var(--primary-blue, #007bff)",
  },
  /**
   * Whether to cover the full screen or just the parent container
   */
  fullScreen: {
    type: Boolean,
    default: false,
  },
  /**
   * Custom aria-label for accessibility
   */
  ariaLabel: {
    type: String,
    default: "Loading content, please wait",
  },
  /**
   * Whether to teleport the overlay to the body element
   */
  teleportToBody: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether this is being used in admin layout (respects TopSectionSpanning)
   */
  adminLayout: {
    type: Boolean,
    default: false,
  },
  /**
   * Height of the top section to offset the overlay
   */
  topSectionHeight: {
    type: String,
    default: "40px",
  },
});

// Computed styles for admin layout positioning
const adminLayoutStyles = computed(() => {
  if (!props.teleportToBody || !props.adminLayout) {
    return {};
  }

  return {
    top: props.topSectionHeight,
    left: "250px", // AdminSidebar width
    width: "calc(100% - 250px)",
    height: `calc(100vh - ${props.topSectionHeight})`,
    "--mobile-left": "0px",
    "--mobile-width": "100%",
  };
});
</script>

<style scoped>
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99;
  backdrop-filter: blur(2px);
}

.loading-overlay.teleported {
  position: fixed;
  z-index: 1000;
}

.loading-overlay.full-screen {
  position: fixed;
  z-index: 9999;
}

.loading-overlay.admin-layout.teleported {
  /* Use computed styles for admin layout positioning */
}

.loading-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
  max-width: 300px;
  padding: 20px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid var(--spinner-color);
  border-radius: 50%;
  animation: loading-spin 1s linear infinite;
}

.loading-text {
  font-size: var(--fs-medium);
  color: #333;
  font-weight: 500;
  margin: 0;
}

.loading-subtitle {
  font-size: var(--fs-small);
  color: #666;
  margin: 0;
  line-height: 1.4;
}

/* Smooth fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes loading-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Mobile responsive styles for admin layout */
@media (max-width: 991px) {
  .loading-overlay.admin-layout.teleported {
    left: var(--mobile-left) !important;
    width: var(--mobile-width) !important;
  }
}

/* Responsive design */
@media (max-width: 768px) {
  .loading-content {
    padding: 15px;
    gap: 15px;
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border-width: 3px;
  }

  .loading-text {
    font-size: var(--fs-small);
  }

  .loading-subtitle {
    font-size: 12px;
  }
}

/* Reduced motion preference */
@media (prefers-reduced-motion: reduce) {
  .loading-spinner {
    animation: none;
    border-top-color: transparent;
    border-left-color: var(--spinner-color);
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: none;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .loading-overlay {
    background-color: rgba(255, 255, 255, 0.98);
  }

  .loading-text {
    color: #000;
  }

  .loading-subtitle {
    color: #333;
  }
}
</style>
